import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { mergeParticipationWithOrg } from './sponsorOrgUtils';

/**
 * Per-event participation rows (same shape as legacy; may include organizationId).
 */
export async function fetchSponsorParticipationsForEvent(eventId) {
  if (!eventId) return [];
  const q = query(collection(firestore, 'sponsors'), where('eventId', '==', eventId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Participations merged with sponsorOrganizations when organizationId is set (public / display).
 */
export async function fetchSponsorsMergedForEvent(eventId) {
  const raw = await fetchSponsorParticipationsForEvent(eventId);
  const orgIds = [...new Set(raw.map((s) => s.organizationId).filter(Boolean))];
  const orgById = await fetchSponsorOrganizationsByIds(orgIds);
  return raw.map((s) => mergeParticipationWithOrg(s, orgById));
}

/**
 * Load sponsor organizations by id (chunked getDoc).
 */
export async function fetchSponsorOrganizationsByIds(organizationIds) {
  const ids = [...new Set((organizationIds || []).filter(Boolean))];
  const out = new Map();
  await Promise.all(
    ids.map(async (id) => {
      const snap = await getDoc(doc(firestore, 'sponsorOrganizations', id));
      if (snap.exists()) {
        out.set(id, { ...snap.data(), id: snap.id });
      }
    })
  );
  return out;
}

/**
 * Prizes for a directory org (optionally scoped to one event).
 */
export async function fetchPrizesForOrganization(organizationId, eventId = null) {
  if (!organizationId) return [];
  let q = query(collection(firestore, 'prizes'), where('organizationId', '==', organizationId));
  if (eventId) {
    q = query(
      collection(firestore, 'prizes'),
      where('organizationId', '==', organizationId),
      where('eventId', '==', eventId)
    );
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * First matching org by normalizedName (for import / dedupe).
 */
export async function findSponsorOrganizationByNormalizedName(normalizedName) {
  const key = (normalizedName || '').trim();
  if (!key) return null;
  const q = query(
    collection(firestore, 'sponsorOrganizations'),
    where('normalizedName', '==', key)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { ...d.data(), id: d.id };
}

/**
 * All directory organizations (admin; use sparingly).
 */
export async function fetchAllSponsorOrganizations() {
  const snap = await getDocs(collection(firestore, 'sponsorOrganizations'));
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}
